import {
  type GenerationRatio,
  isModelCompatibleTool,
  type ModelCompatibleTool,
} from "../../Board/types.ts";
import { CREATE_DEFAULT_TOOL } from "../../Board/Workspaces/constants.ts";
import {
  type BoardModelPickerState,
  boardModelPickerStore,
} from "../stores/boardModelPickerStore.ts";
import {
  type DefaultTool,
  defaultToolStore,
} from "../stores/defaultToolStore.ts";
import { selectedAssetUuidStore } from "../stores/selectedAssetUuidStore.ts";
import { toolToSelectedModelsStorage } from "../stores/selectedModels/toolToSelectedModelsStorage.ts";
import { briefSettingsStorage } from "../stores/settings/briefSettingsStorage.ts";
import { editImageWithPromptSettingsStorage } from "../stores/settings/editImageWithPromptSettingsStorage.ts";
import { expandSettingsStorage } from "../stores/settings/expandSettingsStorage.ts";
import { generateSettingsStorage } from "../stores/settings/generateSettingsStorage.ts";
import type { BoardState } from "../types.ts";

export const setBoardState = (
  boardUuid: string,
  boardState: Partial<BoardState>,
) => {
  if ("modelPickerState" in boardState) {
    setModelPickerState(boardState.modelPickerState);
  }

  if ("selectedAssetUuid" in boardState) {
    setSelectedAssetUuid(boardState.selectedAssetUuid);
  }

  if ("defaultTool" in boardState) setDefaultTool(boardState.defaultTool);

  if ("modelUuids" in boardState && boardState.defaultTool) {
    const createTool = boardState.defaultTool.defaultCreateTool;
    const editTool = boardState.defaultTool.defaultEditTool;

    const modelCompatibleCreateTool =
      createTool && isModelCompatibleTool(createTool) ? createTool : undefined;

    const modelCompatibleEditTool =
      editTool && isModelCompatibleTool(editTool) ? editTool : undefined;

    if (
      modelCompatibleCreateTool !== undefined ||
      modelCompatibleEditTool !== undefined
    ) {
      setSelectedModelUuids(
        boardUuid,
        boardState.modelUuids,
        modelCompatibleCreateTool,
        modelCompatibleEditTool,
      );
    }
  }

  if ("defaultGenerationRatio" in boardState) {
    setDefaultGenerationRatio(boardUuid, boardState.defaultGenerationRatio);
  }

  if ("prompt" in boardState) {
    setPrompt(boardUuid, boardState.prompt, boardState.defaultTool);
  }
};

const setSelectedAssetUuid = (selectedAssetUuid?: string) => {
  if (selectedAssetUuid) {
    selectedAssetUuidStore.setSelectedAssetUuid(selectedAssetUuid);
  } else {
    selectedAssetUuidStore.clearSelectedAssetUuid();
  }
};

const setModelPickerState = (
  modelPickerState?: Partial<BoardModelPickerState>,
) => {
  if (modelPickerState) {
    boardModelPickerStore.setState(modelPickerState);
  } else {
    boardModelPickerStore.reset();
  }
};

const setSelectedModelUuids = (
  boardUuid: string,
  modelUuids?: string[],
  defaultCreateTool?: ModelCompatibleTool,
  defaultEditTool?: ModelCompatibleTool,
) => {
  // We chose to select first the default edit tool, then the default create tool,
  const tool = defaultEditTool ?? defaultCreateTool ?? CREATE_DEFAULT_TOOL;
  const selectedModelsStorage = toolToSelectedModelsStorage[tool];
  if (modelUuids && modelUuids.length > 0) {
    selectedModelsStorage.generationParamsStore.resetModelSelections({
      boardUuid,
    });
    for (const modelUuid of modelUuids) {
      selectedModelsStorage.generationParamsStore.addModelToSelection({
        boardUuid,
        modelUuid,
        // FIXME: This is only valid when passing trained references in the board state.
        // For non-trained references, we would need to have more information about the model,
        // and this should be done at the tool level.
        isModelTrained: true,
      });
    }
  } else {
    selectedModelsStorage.generationParamsStore.resetModelSelections({
      boardUuid,
    });
  }
};

const setDefaultGenerationRatio = (
  boardUuid: string,
  generationRatio?: GenerationRatio,
) => {
  // FIXME: This function should raise an error if the generation ratio is not a valid key of the tool settings store
  for (const store of [
    generateSettingsStorage.settingsStore,
    briefSettingsStorage.settingsStore,
    expandSettingsStorage.settingsStore,
  ]) {
    store.updateSettings(boardUuid, {
      generation_ratio: generationRatio ?? undefined,
    });
  }
};

const setDefaultTool = (defaultTool?: Partial<DefaultTool>) => {
  if (defaultTool) {
    defaultToolStore.setTool(defaultTool);
  } else {
    defaultToolStore.clearTool();
  }
};

const setPrompt = (
  boardUuid: string,
  prompt?: string,
  defaultTool?: DefaultTool,
) => {
  const tool =
    defaultTool?.defaultEditTool ??
    defaultTool?.defaultCreateTool ??
    CREATE_DEFAULT_TOOL;
  if (prompt) {
    if (tool === "brief") {
      briefSettingsStorage.settingsStore.updateSettings(boardUuid, {
        prompt,
      });
    } else if (tool === "generate") {
      generateSettingsStorage.settingsStore.updateSettings(boardUuid, {
        prompt,
      });
    } else if (tool === "edit-image-with-prompt") {
      editImageWithPromptSettingsStorage.settingsStore.updateSettings(
        boardUuid,
        {
          prompt,
        },
      );
    }
  } else {
    if (tool === "brief") {
      briefSettingsStorage.settingsStore.updateSettings(boardUuid, {
        prompt: undefined,
      });
    } else if (tool === "generate") {
      generateSettingsStorage.settingsStore.updateSettings(boardUuid, {
        prompt: undefined,
      });
    } else if (tool === "edit-image-with-prompt") {
      editImageWithPromptSettingsStorage.settingsStore.updateSettings(
        boardUuid,
        {
          prompt: undefined,
        },
      );
    }
  }
};
